/* eslint-disable @typescript-eslint/no-explicit-any */
import { GridActionsCellItem } from '@mui/x-data-grid/components';
import { GridColDef } from '@mui/x-data-grid/models/colDef/gridColDef';
import { Delete, Visibility } from '@mui/icons-material';
import { Box, Tooltip, IconButton } from '@mui/material';
import { Link } from 'react-router-dom';
import { ServiceResponse } from '@/api/bol';
import {
  formatDateTime,
  getEntityDisplayName,
  formatLocation,
  getTrackingIds,
  getTrackingUrls,
  getLobQuotationIds,
  formatExternalReferences,
  formatSpecialCharacteristics,
  formatCurrency,
  joinUniqueValues,
  renderBooleanChip,
  renderChip,
} from '@/utils/datagridFormatters';

interface ServiceColumnsProps {
  onViewDetails?: (serviceId: string) => void;
  onDelete: (service: ServiceResponse) => void;
  canDelete?: boolean;
  deleteTooltip?: string;
}

export const createServiceColumns = ({ onViewDetails: _onViewDetails, onDelete, canDelete = true, deleteTooltip = '' }: ServiceColumnsProps): GridColDef[] => [
  {
    field: 'id',
    headerName: 'ID',
    flex: 1.5,
    minWidth: 200,
    valueGetter: (value: any, row: { id: any; }) => {
      return row.id || '-';
    },
  },
  {
    field: 'serviceType',
    headerName: 'Service Type',
    flex: 1.2,
    minWidth: 150,
    renderCell: (params: { row: { serviceType: string | null | undefined; }; }) => renderChip('serviceType', params.row.serviceType),
  },
  {
    field: 'serviceSubtype',
    headerName: 'Service Subtype',
    flex: 1.2,
    minWidth: 150,
    renderCell: (params: { row: { serviceSubtype: string | null | undefined; }; }) => renderChip('serviceSubtype', params.row.serviceSubtype),
  },
  {
    field: 'trackingUrls',
    headerName: 'Tracking URLs',
    flex: 1.5,
    minWidth: 200,
    valueGetter: (value: any, row: { externalReferences: { externalReferenceType?: string | null; reference?: string | null; }[] | null | undefined; }) => {
      return getTrackingUrls(row.externalReferences);
    },
    renderCell: (params: { row: { externalReferences: { externalReferenceType?: string | null; reference?: string | null; }[] | null | undefined; }; }) => {
      const trackingUrls = getTrackingUrls(params.row.externalReferences);
      if (trackingUrls === '-') {
        return '-';
      }
      
      // Split multiple URLs and render as clickable links
      const urls = trackingUrls.split(', ').filter(url => url.trim());
      
      return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, py: 0.5 }}>
          {urls.map((url) => (
            <a
              key={url}
              href={url.startsWith('http') ? url : `https://${url}`}
              target="_blank"
              rel="noopener noreferrer"
              style={{
                color: '#1976d2',
                textDecoration: 'none',
                fontSize: '0.875rem',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                maxWidth: '100%',
                display: 'block'
              }}
              onMouseEnter={(e) => {
                (e.target as HTMLElement).style.textDecoration = 'underline';
              }}
              onMouseLeave={(e) => {
                (e.target as HTMLElement).style.textDecoration = 'none';
              }}
            >
              {url}
            </a>
          ))}
        </Box>
      );
    },
  },
  {
    field: 'trackingIds',
    headerName: 'Tracking IDs',
    flex: 1.5,
    minWidth: 200,
    valueGetter: (value: any, row: { externalReferences: { externalReferenceType?: string | null; reference?: string | null; }[] | null | undefined; }) => {
      return getTrackingIds(row.externalReferences);
    },
  },
  {
    field: 'lobQuotationIds',
    headerName: 'LOB Quotation IDs',
    flex: 1.5,
    minWidth: 200,
    valueGetter: (value: any, row: { externalReferences: { externalReferenceType?: string | null; reference?: string | null; }[] | null | undefined; }) => {
      return getLobQuotationIds(row.externalReferences);
    },
  },
  // Existing columns - hidden by default
  {
    field: 'serviceName',
    headerName: 'Name',
    flex: 1.2,
    minWidth: 150,
    valueGetter: (value: any, row: { serviceName: any; }) => {
      return row.serviceName || '-';
    },
  },
  {
    field: 'submissionDateTime',
    headerName: 'Submission Date',
    flex: 1.3,
    minWidth: 180,
    valueGetter: (value: any, row: { submissionDateTime: string | null | undefined; }) => {
      return formatDateTime(row.submissionDateTime);
    },
  },
  {
    field: 'serviceStatus',
    headerName: 'Status',
    flex: 1,
    minWidth: 120,
    valueGetter: (value: any, row: { serviceStatus: any; }) => {
      return row.serviceStatus || '-';
    },
  },
  {
    field: 'serviceProcess',
    headerName: 'Process',
    flex: 1,
    minWidth: 120,
    valueGetter: (value: any, row: { serviceProcess: any; }) => {
      return row.serviceProcess || '-';
    },
  },
  {
    field: 'requestor',
    headerName: 'Requestor',
    flex: 1.2,
    minWidth: 150,
    valueGetter: (value: any, row: { requestor: { commercialName?: string | null; legalEntityName?: string | null; name?: string | null; } | null | undefined; }) => {
      return getEntityDisplayName(row.requestor);
    },
  },
  {
    field: 'provider',
    headerName: 'Provider',
    flex: 1.2,
    minWidth: 150,
    valueGetter: (value: any, row: { provider: { commercialName?: string | null; legalEntityName?: string | null; name?: string | null; } | null | undefined; }) => {
      return getEntityDisplayName(row.provider);
    },
  },
  {
    field: 'startLocation',
    headerName: 'Origin',
    flex: 1.2,
    minWidth: 150,
    valueGetter: (value: any, row: { startLocation: { location?: { address?: { addressLocality?: string | null; postalCode?: string | null; } | null; } | null; } | null | undefined; }) => {
      return formatLocation(row.startLocation);
    },
  },
  {
    field: 'endLocation',
    headerName: 'Destination',
    flex: 1.2,
    minWidth: 150,
    valueGetter: (value: any, row: { endLocation: { location?: { address?: { addressLocality?: string | null; postalCode?: string | null; } | null; } | null; } | null | undefined; }) => {
      return formatLocation(row.endLocation);
    },
  },
  {
    field: 'validityEndDateTime',
    headerName: 'Valid Until',
    flex: 1.3,
    minWidth: 180,
    valueGetter: (value: any, row: { validityEndDateTime: string | null | undefined; }) => {
      return formatDateTime(row.validityEndDateTime);
    },
  },
  {
    field: 'batchesCount',
    headerName: 'Batches',
    flex: 0.8,
    minWidth: 100,
    valueGetter: (value: any, row: { batchesOfGoods: string | any[]; }) => {
      return row.batchesOfGoods?.length || 0;
    },
  },
  // New columns - hidden by default
  {
    field: 'executionStatus',
    headerName: 'Execution Status',
    flex: 1,
    minWidth: 120,
    valueGetter: (value: any, row: { executionStatus: any; }) => {
      return row.executionStatus || '-';
    },
  },
  {
    field: 'invoiceStatus',
    headerName: 'Invoice Status',
    flex: 1,
    minWidth: 120,
    valueGetter: (value: any, row: { invoiceStatus: any; }) => {
      return row.invoiceStatus || '-';
    },
  },
  {
    field: 'isDangerous',
    headerName: 'Dangerous Goods',
    flex: 1,
    minWidth: 120,
    renderCell: (params: { row: { isDangerous: boolean | null | undefined; }; }) => renderBooleanChip(params.row.isDangerous),
  },
  {
    field: 'serviceNature',
    headerName: 'Service Nature',
    flex: 1,
    minWidth: 120,
    valueGetter: (value: any, row: { serviceNature: any; }) => {
      return row.serviceNature || '-';
    },
  },
  {
    field: 'serviceDescription',
    headerName: 'Description',
    flex: 1.5,
    minWidth: 200,
    valueGetter: (value: any, row: { serviceDescription: string; }) => {
      const desc = row.serviceDescription || '-';
      return desc.length > 100 ? desc.substring(0, 97) + '...' : desc;
    },
  },
  {
    field: 'serviceAgreementReference',
    headerName: 'Agreement Reference',
    flex: 1.2,
    minWidth: 150,
    valueGetter: (value: any, row: { serviceAgreementReference: any; }) => {
      return row.serviceAgreementReference || '-';
    },
  },
  {
    field: 'productDescription',
    headerName: 'Product Description',
    flex: 1.5,
    minWidth: 200,
    valueGetter: (value: any, row: { productDescription: any; }) => {
      return row.productDescription || '-';
    },
  },
  {
    field: 'slaRequested',
    headerName: 'SLA Requested',
    flex: 1,
    minWidth: 120,
    valueGetter: (value: any, row: { sla: { requestedSLA: any; }; }) => {
      return row.sla?.requestedSLA || '-';
    },
  },
  {
    field: 'slaPlanned',
    headerName: 'SLA Planned',
    flex: 1,
    minWidth: 120,
    valueGetter: (value: any, row: { sla: { plannedSLA: any; }; }) => {
      return row.sla?.plannedSLA || '-';
    },
  },
  {
    field: 'slaActual',
    headerName: 'SLA Actual',
    flex: 1,
    minWidth: 120,
    valueGetter: (value: any, row: { sla: { actualSLA: any; }; }) => {
      return row.sla?.actualSLA || '-';
    },
  },
  {
    field: 'startRequestedEarliest',
    headerName: 'Origin Requested Start',
    flex: 1.3,
    minWidth: 180,
    valueGetter: (value: any, row: { startLocation: { requestedStartTimeRange: { earliestDateTime: string | null | undefined; }; }; }) => {
      return formatDateTime(row.startLocation?.requestedStartTimeRange?.earliestDateTime);
    },
  },
  {
    field: 'startRequestedLatest',
    headerName: 'Origin Requested End',
    flex: 1.3,
    minWidth: 180,
    valueGetter: (value: any, row: { startLocation: { requestedEndTimeRange: { latestDateTime: string | null | undefined; }; }; }) => {
      return formatDateTime(row.startLocation?.requestedEndTimeRange?.latestDateTime);
    },
  },
  {
    field: 'startPlannedEarliest',
    headerName: 'Origin Planned Start',
    flex: 1.3,
    minWidth: 180,
    valueGetter: (value: any, row: { startLocation: { plannedStartTimeRange: { earliestDateTime: string | null | undefined; }; }; }) => {
      return formatDateTime(row.startLocation?.plannedStartTimeRange?.earliestDateTime);
    },
  },
  {
    field: 'startPlannedLatest',
    headerName: 'Origin Planned End',
    flex: 1.3,
    minWidth: 180,
    valueGetter: (value: any, row: { startLocation: { plannedEndTimeRange: { latestDateTime: string | null | undefined; }; }; }) => {
      return formatDateTime(row.startLocation?.plannedEndTimeRange?.latestDateTime);
    },
  },
  {
    field: 'startActualStart',
    headerName: 'Origin Actual Start',
    flex: 1.3,
    minWidth: 180,
    valueGetter: (value: any, row: { startLocation: { actualStartDateTime: string | null | undefined; }; }) => {
      return formatDateTime(row.startLocation?.actualStartDateTime);
    },
  },
  {
    field: 'startActualEnd',
    headerName: 'Origin Actual End',
    flex: 1.3,
    minWidth: 180,
    valueGetter: (value: any, row: { startLocation: { actualEndDateTime: string | null | undefined; }; }) => {
      return formatDateTime(row.startLocation?.actualEndDateTime);
    },
  },
  {
    field: 'startActionTypes',
    headerName: 'Origin Action Types',
    flex: 1.2,
    minWidth: 150,
    valueGetter: (value: any, row: { startLocation: { actions: { actionType?: string; }[]; }; }) => {
      const types = row.startLocation?.actions?.map((action: { actionType?: string }) => action.actionType).filter(Boolean);
      return joinUniqueValues(types);
    },
  },
  {
    field: 'endRequestedEarliest',
    headerName: 'Destination Requested Start',
    flex: 1.3,
    minWidth: 180,
    valueGetter: (value: any, row: { endLocation: { requestedStartTimeRange: { earliestDateTime: string | null | undefined; }; }; }) => {
      return formatDateTime(row.endLocation?.requestedStartTimeRange?.earliestDateTime);
    },
  },
  {
    field: 'endRequestedLatest',
    headerName: 'Destination Requested End',
    flex: 1.3,
    minWidth: 180,
    valueGetter: (value: any, row: { endLocation: { requestedEndTimeRange: { latestDateTime: string | null | undefined; }; }; }) => {
      return formatDateTime(row.endLocation?.requestedEndTimeRange?.latestDateTime);
    },
  },
  {
    field: 'endPlannedEarliest',
    headerName: 'Destination Planned Start',
    flex: 1.3,
    minWidth: 180,
    valueGetter: (value: any, row: { endLocation: { plannedStartTimeRange: { earliestDateTime: string | null | undefined; }; }; }) => {
      return formatDateTime(row.endLocation?.plannedStartTimeRange?.earliestDateTime);
    },
  },
  {
    field: 'endPlannedLatest',
    headerName: 'Destination Planned End',
    flex: 1.3,
    minWidth: 180,
    valueGetter: (value: any, row: { endLocation: { plannedEndTimeRange: { latestDateTime: string | null | undefined; }; }; }) => {
      return formatDateTime(row.endLocation?.plannedEndTimeRange?.latestDateTime);
    },
  },
  {
    field: 'endActualStart',
    headerName: 'Destination Actual Start',
    flex: 1.3,
    minWidth: 180,
    valueGetter: (value: any, row: { endLocation: { actualStartDateTime: string | null | undefined; }; }) => {
      return formatDateTime(row.endLocation?.actualStartDateTime);
    },
  },
  {
    field: 'endActualEnd',
    headerName: 'Destination Actual End',
    flex: 1.3,
    minWidth: 180,
    valueGetter: (value: any, row: { endLocation: { actualEndDateTime: string | null | undefined; }; }) => {
      return formatDateTime(row.endLocation?.actualEndDateTime);
    },
  },
  {
    field: 'endActionTypes',
    headerName: 'Destination Action Types',
    flex: 1.2,
    minWidth: 150,
    valueGetter: (value: any, row: { endLocation: { actions: { actionType?: string; }[]; }; }) => {
      const types = row.endLocation?.actions?.map((action: { actionType?: string }) => action.actionType).filter(Boolean);
      return joinUniqueValues(types);
    },
  },
  {
    field: 'finalLocationName',
    headerName: 'Final Location',
    flex: 1.2,
    minWidth: 150,
    valueGetter: (value: any, row: { finalLocation: { locationName: any; }; }) => {
      return row.finalLocation?.locationName || '-';
    },
  },
  {
    field: 'originLocationName',
    headerName: 'Origin Location Name',
    flex: 1.2,
    minWidth: 150,
    valueGetter: (value: any, row: { originLocation: { locationName: any; }; }) => {
      return row.originLocation?.locationName || '-';
    },
  },
  {
    field: 'invoicedParty',
    headerName: 'Invoiced Party',
    flex: 1.2,
    minWidth: 150,
    valueGetter: (value: any, row: { invoicedParty: { commercialName?: string | null; legalEntityName?: string | null; name?: string | null; } | null | undefined; }) => {
      return getEntityDisplayName(row.invoicedParty);
    },
  },
  {
    field: 'saleOperationsCount',
    headerName: 'Sale Operations',
    flex: 0.8,
    minWidth: 100,
    valueGetter: (value: any, row: { saleOperations: string | any[]; }) => {
      return row.saleOperations?.length || 0;
    },
  },
  {
    field: 'saleOperationTypes',
    headerName: 'Operation Types',
    flex: 1.2,
    minWidth: 150,
    valueGetter: (value: any, row: { saleOperations: { operationType?: string; }[]; }) => {
      const types = row.saleOperations?.map((op: { operationType?: string }) => op.operationType).filter(Boolean);
      return joinUniqueValues(types);
    },
  },
  {
    field: 'totalOperationAmount',
    headerName: 'Total Operation Amount',
    flex: 1.2,
    minWidth: 150,
    valueGetter: (value: any, row: { totalOperationAmount: { value: number | null | undefined; unit: string | null | undefined; }; }) => {
      return formatCurrency(row.totalOperationAmount?.value, row.totalOperationAmount?.unit);
    },
  },
  {
    field: 'declaredValue',
    headerName: 'Declared Value',
    flex: 1,
    minWidth: 120,
    valueGetter: (value: any, row: { declaredValue: { value: number | null | undefined; unit: string | null | undefined; }; }) => {
      return formatCurrency(row.declaredValue?.value, row.declaredValue?.unit);
    },
  },
  {
    field: 'incoterm',
    headerName: 'Incoterm',
    flex: 0.8,
    minWidth: 100,
    valueGetter: (value: any, row: { incoterm: any; }) => {
      return row.incoterm || '-';
    },
  },
  {
    field: 'contactsCount',
    headerName: 'Contacts',
    flex: 0.8,
    minWidth: 100,
    valueGetter: (value: any, row: { contacts: string | any[]; }) => {
      return row.contacts?.length || 0;
    },
  },
  {
    field: 'contactRoles',
    headerName: 'Contact Roles',
    flex: 1.2,
    minWidth: 150,
    valueGetter: (value: any, row: { contacts: { role?: string; }[]; }) => {
      const roles = row.contacts?.map((contact: { role?: string }) => contact.role).filter(Boolean);
      return joinUniqueValues(roles);
    },
  },
  {
    field: 'documentsCount',
    headerName: 'Documents',
    flex: 0.8,
    minWidth: 100,
    valueGetter: (value: any, row: { documents: string | any[]; }) => {
      return row.documents?.length || 0;
    },
  },
  {
    field: 'externalReferencesOther',
    headerName: 'Other External References',
    flex: 1.5,
    minWidth: 200,
    valueGetter: (value: any, row: { externalReferences: any[]; }) => {
      // Filter out tracking-related references
      const otherRefs = row.externalReferences?.filter(
        (ref: { externalReferenceType?: string }) => 
          !ref.externalReferenceType?.includes('_TRACKING_ID') &&
          !ref.externalReferenceType?.includes('_TRACKING_URL') &&
          !ref.externalReferenceType?.includes('_QUOTATION_ID')
      );
      return formatExternalReferences(otherRefs);
    },
  },
  {
    field: 'specialInstructionsCount',
    headerName: 'Special Instructions',
    flex: 1,
    minWidth: 120,
    valueGetter: (value: any, row: { specialInstructions: string | any[]; }) => {
      return row.specialInstructions?.length || 0;
    },
  },
  {
    field: 'specialCharacteristics',
    headerName: 'Special Characteristics',
    flex: 1.5,
    minWidth: 200,
    valueGetter: (value: any, row: { specialCharacteristics: { specialCharacteristicType?: string | null; characteristicReference?: string | null; stringValue?: string | null; numericalValue?: { value?: number; unit?: string; } | null; }[] | null | undefined; }) => {
      return formatSpecialCharacteristics(row.specialCharacteristics);
    },
  },
  {
    field: 'serviceRequestsLink',
    headerName: 'Service Requests',
    flex: 1.2,
    minWidth: 150,
    valueGetter: (value: any, row: { serviceRequests: { href: any; }; }) => {
      const link = row.serviceRequests?.href || row.serviceRequests;
      return link || '-';
    },
  },
  {
    field: 'scheduledActivitiesLink',
    headerName: 'Scheduled Activities',
    flex: 1.2,
    minWidth: 150,
    valueGetter: (value: any, row: { scheduledActivities: { href: any; }; }) => {
      const link = row.scheduledActivities?.href || row.scheduledActivities;
      return link || '-';
    },
  },
  {
    field: 'consignmentsLink',
    headerName: 'Consignments',
    flex: 1.2,
    minWidth: 150,
    valueGetter: (value: any, row: { consignments: { href: any; }; }) => {
      const link = row.consignments?.href || row.consignments;
      return link || '-';
    },
  },
  {
    field: 'actions',
    type: 'actions',
    headerName: 'Actions',
    flex: 1,
    minWidth: 120,
    hideable: false, // Always visible
    getActions: (params: { row: ServiceResponse; }) => [
      <IconButton
        key="view"
        component={Link}
        to={`/services/${params.row.id}`}
        size="small"
        color="primary"
        aria-label="View Details"
        sx={{ padding: '8px' }}
      >
        <Visibility fontSize="small" />
      </IconButton>,
      <Tooltip title={!canDelete ? deleteTooltip : ''} key="delete-tooltip">
        <span>
          <GridActionsCellItem
            icon={<Delete />}
            label="Delete"
            onClick={() => onDelete(params.row)}
            disabled={!canDelete}
            color="error"
          />
        </span>
      </Tooltip>,
    ],
  },
];

export const defaultServiceColumnVisibilityModel = {
  // Default visible columns
  id: true,
  serviceType: true,
  serviceSubtype: true,
  trackingUrls: true,
  trackingIds: true,
  lobQuotationIds: true,
  actions: true,
  // Hidden by default
  serviceName: false,
  submissionDateTime: false,
  serviceStatus: false,
  serviceProcess: false,
  requestor: false,
  provider: false,
  startLocation: false,
  endLocation: false,
  validityEndDateTime: false,
  batchesCount: false,
  executionStatus: false,
  invoiceStatus: false,
  isDangerous: false,
  serviceNature: false,
  serviceDescription: false,
  serviceAgreementReference: false,
  productDescription: false,
  slaRequested: false,
  slaPlanned: false,
  slaActual: false,
  startRequestedEarliest: false,
  startRequestedLatest: false,
  startPlannedEarliest: false,
  startPlannedLatest: false,
  startActualStart: false,
  startActualEnd: false,
  startActionTypes: false,
  endRequestedEarliest: false,
  endRequestedLatest: false,
  endPlannedEarliest: false,
  endPlannedLatest: false,
  endActualStart: false,
  endActualEnd: false,
  endActionTypes: false,
  finalLocationName: false,
  originLocationName: false,
  invoicedParty: false,
  saleOperationsCount: false,
  saleOperationTypes: false,
  totalOperationAmount: false,
  declaredValue: false,
  incoterm: false,
  contactsCount: false,
  contactRoles: false,
  documentsCount: false,
  externalReferencesOther: false,
  specialInstructionsCount: false,
  specialCharacteristics: false,
  serviceRequestsLink: false,
  scheduledActivitiesLink: false,
  consignmentsLink: false,
};